/**
 * The bodies of `POST /api/dashboard/results` (and `/excel`) for "Price curve of a date" (CU-869fmjzrg). Only what
 * changes the data travels: Min / Avg / Max, Tier / Providers, the dotted line and the view of the screen are local.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { carsBody } from '../../../@services/cars.js'
import { pickupIso } from './pickup.js'

export const REPORT = Object.freeze({
	curve: 'price-curve', provider: 'price-curve-provider', day: 'price-curve-day', weekly: 'price-curve-weekly',
})
export const SPLITS = Object.freeze(['category', 'acriss'])

const compact = iso => String(iso).replaceAll('-', '')

/** The curve of the view: office, length, currency, cars, the pick-up (a date, or a day of the week) and same date */
export function curveBody({ view, currency, today }) {
	const pickup = pickupIso(view, today)
	if (!view?.office || !pickup) return null
	return {
		reportType: REPORT.curve, officePickupID: view.office, lor: view.lor, currency: currency ?? null,
		sameWeekday: !view.same, isNewVersion: true, pick: view.pick, pickup, ...carsBody(view),
	}
}

export function providerBody(args, providerId, split = 'category') {
	const body = curveBody(args)
	return body && { ...body, reportType: REPORT.provider, providerId: Number(providerId), split: SPLITS.includes(split) ? split : 'category' }
}

export function dayBody(args, d) {
	const body = curveBody(args)
	return body && Number.isInteger(d) ? { ...body, reportType: REPORT.day, curveDistance: d } : null
}

/** Week by week: several offices (or office groups), pick-up dates and capture weeks; sites, transmission, currency */
export function weeklyBody({ view, currency }) {
	if (!view?.offices?.length || !view?.dates?.length) return null
	const body = {
		reportType: REPORT.weekly, lor: view.lor, currency: view.cur ?? currency ?? null,
		pickupDate: [...view.dates].sort().map(compact), captureWeeks: view.weeks === 'default' ? 'default' : [...view.weeks],
		isDailyPrice: view.price === 'daily', ...carsBody(view),
	}
	if (view.okind === 'groups') body.officeGroupID = [...view.offices]
	else body.officePickupID = [...view.offices]
	if (view.sites?.length) {
		if (view.skind === 'siteGroups') body.siteGroupID = [...view.sites]
		else if (view.skind === 'markets') body.market = [...view.sites]
		else body.siteID = [...view.sites]
	}
	if (view.trans?.length) body.automatic = [...view.trans]
	return body
}

/** The Excel of Week by week is the screen: its statistic and the providers shown (`null` = all) */
export function weeklyExcelBody(body, stat, providerIds = null) {
	return body && { ...body, tabKey: stat, ...(providerIds?.length ? { providerID: [...providerIds].map(Number).sort((a, b) => a - b) } : {}) }
}

export const bodyKey = body => (body ? JSON.stringify(body) : null)
